import { ParticipantVideoDisableReason } from './constants';
import { SignalingEventType } from './constants';
import type {
  OTSignalEvent,
  OTVideoDisableReason,
  SignalEvent,
  TransmitUsernameSignalEvent,
  TransmitNetworkQualitySignalEvent,
  ToggleCaptionsSignalEvent,
  CaptionsStatusServerEvent,
} from './types';

/**
 * Validates the original OT SignalEvent and returns the SignalEvent if it is valid
 */
export const parseOTSignalEvent = (
  event: OTSignalEvent,
): SignalEvent | null => {
  if (event.data === undefined || event.type === undefined) {
    return null;
  }

  const eventType = event.type.split('signal:')[1];
  if (eventType === undefined) {
    return null;
  }

  /**
   * Handle server events.
   * If event comes from the server `from` will be `null`.
   * @link https://developer.vonage.com/en/video/how-tos/signaling/index#:~:text=Note%20that%20you%20can%20send%20Signals%20from%20your%20server%2C%20instead%20of%20from%20a%20client%20connected%20to%20the%20session.%20In%20this%20case%2C%20the%20from%20property%20is%20set%20to%20null.
   */
  if (event.from === null) {
    switch (eventType) {
      case SignalingEventType.CaptionsStatus: {
        const captionsStatusServerEvent: CaptionsStatusServerEvent = {
          type: eventType,
          data: event.data as CaptionsStatusServerEvent['data'],
          from: event.from,
        };
        return captionsStatusServerEvent;
      }
      default: {
        return null;
      }
    }
  }

  switch (eventType) {
    case SignalingEventType.TransmitUsername: {
      const transmitUsernameSignalEvent: TransmitUsernameSignalEvent = {
        type: eventType,
        data: event.data,
        from: event.from,
      };
      return transmitUsernameSignalEvent;
    }
    case SignalingEventType.ToggleCaptions: {
      const ToggleCaptionsSignalEvent: ToggleCaptionsSignalEvent = {
        type: eventType,
        data: event.data as 'off' | 'on',
        from: event.from,
      };
      return ToggleCaptionsSignalEvent;
    }
    case SignalingEventType.TransmitNetworkQuality: {
      const transmitNetworkQualitySignalEvent: TransmitNetworkQualitySignalEvent =
        {
          type: eventType,
          data: event.data,
          from: event.from,
        };
      return transmitNetworkQualitySignalEvent;
    }
    default: {
      return null;
    }
  }
};

/**
 * Converts OTVideoDisableReason to ParticipantVideoDisableReason.
 */
export const getParticipantVideoDisableReason = (
  reason: OTVideoDisableReason,
): ParticipantVideoDisableReason => {
  switch (reason) {
    case 'codecNotSupported': {
      return ParticipantVideoDisableReason.Other;
    }
    case 'publishVideo': {
      return ParticipantVideoDisableReason.NotPublished;
    }
    case 'quality': {
      return ParticipantVideoDisableReason.PoorQuality;
    }
    case 'subscribeToVideo': {
      return ParticipantVideoDisableReason.Unsubscribed;
    }
    default: {
      return ParticipantVideoDisableReason.Other;
    }
  }
};
